import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { storesAPI, extractErrorMessage } from '../api';
import { useAuth } from './AuthContext';

const StoreContext = createContext(null);

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
};

export const StoreProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadingRef = useRef(false);

  const loadStores = useCallback(async () => {
    if (loadingRef.current) return;          // prevent concurrent calls
    loadingRef.current = true;
    try {
      setLoading(true);
      setError(null);
      const response = await storesAPI.list();
      setStores(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('[StoreContext] loadStores error:', err?.response?.status, err?.message);
      setError(extractErrorMessage(err, 'Failed to load stores'));
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadStores();

      // Load saved store from localStorage
      try {
        const savedStore = localStorage.getItem('selectedStore');
        if (savedStore) {
          setSelectedStore(JSON.parse(savedStore));
        }
      } catch (e) {
        localStorage.removeItem('selectedStore');
      }
    } else {
      setStores([]);
      setSelectedStore(null);
    }
  }, [isAuthenticated, loadStores]);

  const selectStore = (store) => {
    setSelectedStore(store);
    localStorage.setItem('selectedStore', JSON.stringify(store));
  };

  const clearStore = () => {
    setSelectedStore(null);
    localStorage.removeItem('selectedStore');
  };

  const syncStoreInState = (updatedStore) => {
    setStores(prev => prev.map(s => (s.id === updatedStore.id ? updatedStore : s)));
    if (selectedStore?.id === updatedStore.id) {
      setSelectedStore(updatedStore);
      localStorage.setItem('selectedStore', JSON.stringify(updatedStore));
    }
  };

  const createStore = async (storeData) => {
    try {
      setError(null);
      const response = await storesAPI.create(storeData);
      const newStore = response.data;
      setStores(prev => [...prev, newStore]);
      return { success: true, store: newStore };
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to create store');
      return { success: false, error: message };
    }
  };

  const completeOnboarding = async (storeId) => {
    try {
      setError(null);
      const response = await storesAPI.completeOnboarding(storeId);
      const updatedStore = response.data;
      syncStoreInState(updatedStore);
      return { success: true, store: updatedStore };
    } catch (err) {
      const message = extractErrorMessage(err, 'Failed to complete onboarding');
      return { success: false, error: message };
    }
  };

  const isModuleEnabled = (moduleName) => {
    if (!selectedStore || !selectedStore.modules) return false;
    const module = selectedStore.modules.find(m => m.module_name === moduleName);
    return module?.enabled || false;
  };

  // Adaptive module system — derived from store fields added in migration 017.
  // Falls back to safe defaults so existing stores keep working unchanged.
  const businessType = selectedStore?.business_type || 'RETAIL';
  const modulesEnabled = selectedStore?.modules_enabled || {};

  const value = {
    stores,
    selectedStore,
    loading,
    error,
    loadStores,
    selectStore,
    clearStore,
    createStore,
    isModuleEnabled,
    businessType,
    modulesEnabled,
    completeOnboarding,
  };

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
};
